// `extension-contact.tpl.html` — the "Contact us" form the frame opens from the header menu.
//
// Two states in one component: the form, and a thank-you panel after a successful submit
// (`ng-if="ctrl.done"`). The close ✕ sits OUTSIDE both, so it is present in either.
import { createElement, useState, type ReactElement } from 'react'

export interface ExtensionContactProps {
  /** `maxReasonLength` — the textarea's `maxlength`. */
  maxReasonLength?: number
  /** `maxAttachmentAmount` — and the number in the hint. */
  maxAttachmentAmount?: number
  /**
   * `maxAttachmentSizeMb`.
   *
   * ⚠️ UPSTREAM NEVER DEFINES IT. The template interpolates `{{ ::ctrl.maxAttachmentSizeMb }}`
   * and the controller declares `maxReasonLength` and `maxAttachmentAmount` and no third field,
   * so the binding resolves to nothing and the hint renders "Add up to 5 files ( max)" — with
   * the gap. The reference screenshot has that gap. Supplying a plausible "10MB" here would be
   * fixing an upstream bug inside a port, and the diff would show it; the fix belongs upstream,
   * which is what CLAUDE.md asks for. Left undefined, and said out loud.
   */
  maxAttachmentSizeMb?: string
  /**
   * `ExtensionViewWidth.Slim` (200), as a `max-width` on the ELEMENT.
   *
   * ⚠️ A THIRD PROPERTY, AND THE FOURTH COMBINATION. Every extension state supplies its width
   * from the route and no two of them do it the same way:
   *
   *     review        flex-basis: Slim      on <shortlist-extension-alerts>
   *     edit-alert    min-width:  Full      on <shortlist-extension-alerts>
   *     not-found     flex-basis: Extended  on <shortlist-candidate-profile-not-found>
   *     contact       max-width:  Slim      on <extension-contact>
   *
   * So the width cannot be inferred from the screen, the element or the other three: it has to
   * be read off that state's `ng-style` each time. Guessing `flex-basis` here lets the form
   * fill a 378px frame, which is what it did.
   */
  maxWidth?: number
  onClose?: () => void
  onSubmit?: (reason: string) => void
}

export function ExtensionContact({
  maxReasonLength = 5000,
  maxAttachmentAmount = 5,
  maxAttachmentSizeMb,
  maxWidth,
  onClose,
  onSubmit,
}: ExtensionContactProps): ReactElement {
  const [reason, setReason] = useState('')
  const [done, setDone] = useState(false)

  const body = (
    <div className="extension-contact">
      {/* Outside both states — the ✕ closes the thank-you panel too. */}
      <i
        className="close-btn cursor-pointer fas fa-times"
        aria-hidden="true"
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          onClose?.()
        }}
      />

      {done ? (
        <div className="submitted">
          <i className="done-icon fas fa-check-circle" aria-hidden="true" />
          <h1 className="title">Thank you!</h1>
          <p className="description">We will review your request and get back to you shortly.</p>
          {/* Lower-case in the markup; `text-transform` does the rest, as on the Send button. */}
          <button type="button" className="submitted-close-btn cta-btn" onClick={onClose}>
            close
          </button>
        </div>
      ) : (
        <div className="unsubmitted">
          <h1 className="title">Contact us</h1>
          <div className="contact-form">
            <div className="input-group reason-input-group">
              <label htmlFor="reason">How can we help you?</label>
              <textarea
                id="reason"
                name="reason"
                className="reason-input"
                placeholder="Enter your message"
                maxLength={maxReasonLength}
                required
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>

            <div className="input-group attachments-input-group">
              <label htmlFor="attachments">Attachments</label>
              {/* ⚠️ A `<button>`, NOT AN `<input type="file">`. `ngf-select` turns it into the
                  picker; the element the stylesheet sizes is the button. The file plumbing
                  (`ng-file-upload`, the per-file progress rows) lands with the upload service. */}
              <button id="attachments" type="button" className="attachments-input btn btn-primary">
                <span className="upload">
                  <i className="upload-icon fas fa-paperclip" aria-hidden="true" />
                  <span className="upload-title highlighted">Click to upload</span>
                  <span className="upload-description">
                    <span>Add up to {maxAttachmentAmount} files</span> <span>({maxAttachmentSizeMb} max)</span>{' '}
                    <span>JPEG, PNG, GIF, BMP, TIFF</span>
                  </span>
                </span>
              </button>
            </div>

            {/* `ng-disabled="ctrl.form.$invalid"` — and `reason` is the only required field, so
                the button is disabled exactly while the message is empty. */}
            <button
              type="submit"
              className="submit-btn cta-btn"
              disabled={reason.trim() === ''}
              onClick={() => {
                onSubmit?.(reason)
                setDone(true)
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  )

  // The component ELEMENT, which is what the state's `ng-style` targets — the class of the same
  // name is its template root, inside.
  return createElement(
    'extension-contact',
    maxWidth === undefined ? null : { style: { maxWidth: `${String(maxWidth)}px` } },
    body,
  )
}
